import { lazy, Suspense, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import styles from './AppLayout.module.css';

// Lazy-load the live-mode Clerk gate so mockup-mode smoke tests
// (which do NOT mount a ClerkProvider) never pull the Clerk hooks
// into their module graph.
const LiveOrgGate = lazy(() => import('./LiveOrgGate').then((m) => ({ default: m.LiveOrgGate })));

export function AppLayout() {
  // Read `mockupMode` from AppContext, NOT from `config` directly. AppShell
  // gates ClerkProvider on the runtime toggle (AppContext.mockupMode); reading
  // `config.mockupMode` here would diverge the moment the user flips the
  // in-app toggle, mounting LiveOrgGate without a ClerkProvider and crashing
  // `useUser()`. Both gates MUST read from the same source.
  const { sidebarCollapsed, mockupMode } = useAppContext();

  const shell: ReactNode = (
    <div className={styles.shell}>
      <a href="#main" className={styles.skipLink}>
        Skip to main content
      </a>
      <Sidebar />
      <TopBar />
      <main id="main" className={`${styles.main} ${sidebarCollapsed ? styles.mainShifted : ''}`}>
        <Outlet />
      </main>
    </div>
  );

  if (mockupMode) {
    return shell;
  }

  return (
    <Suspense fallback={null}>
      <LiveOrgGate>{shell}</LiveOrgGate>
    </Suspense>
  );
}
